import { create, protoInt64 } from '@bufbuild/protobuf'
import { TimestampSchema } from '@bufbuild/protobuf/wkt'

// google.protobuf.Timestamp の JSON シリアライズ可能範囲 (西暦 1〜9999 年)。
// @bufbuild/protobuf v2 の timestampToJson は範囲外を throw するため、
// API 送信前にこの範囲でユーザー入力を弾く必要がある。
// 例: JST で "0001/01/01" を入力すると UTC では年 0 (-62135629200000 ms) となり範囲外。
// Sentry: https://houscape.sentry.io/issues/7536087044/
//
// リテラル値で定義する理由: Date.parse は古い JS エンジン (Safari の一部バージョン等) で
// "0001-01-01T00:00:00Z" のような極端な日付に対し NaN を返す可能性がある。
// 値は timestampFromDate の seconds = Math.floor(getTime()/1000) を経由して
// timestampToJson の `Number(seconds) * 1000 in [MIN, MAX]` を通過するように選んでいる。
const MIN_PROTO_TIMESTAMP_MS = -62135596800000 // 0001-01-01T00:00:00.000Z
const MAX_PROTO_TIMESTAMP_MS = 253402300799999 // 9999-12-31T23:59:59.999Z

/**
 * Date が以下を満たすかを判定する:
 *   1. `getTime()` が NaN ではない (JavaScript 的に有効)
 *   2. google.protobuf.Timestamp の JSON シリアライズ範囲内 (西暦 1〜9999 年)
 *
 * 2 を満たさない Date を Timestamp に変換すると `toJson` 時に throw されるため、
 * Timestamp として送信される可能性のある入力は必ず本関数でガードする。
 */
export const isValidDate = (date: Date) => {
  const t = date.getTime()
  return !Number.isNaN(t) && t >= MIN_PROTO_TIMESTAMP_MS && t <= MAX_PROTO_TIMESTAMP_MS
}

export const toDate = (date: string | Date | null | undefined): Date | undefined => {
  if (date === null || date === undefined) {
    return undefined
  }
  if (typeof date === 'string') {
    if (date === '') {
      // 空文字の場合
      return undefined
    }
    const d = new Date(date)
    if (!isValidDate(d)) {
      // 無効な日付 / proto 範囲外の場合
      return undefined
    }
    return d
  }
  // Date 型でも proto 範囲外の場合は弾く
  if (!isValidDate(date)) {
    return undefined
  }
  return date
}

// Google Calendar用の日付フォーマットに変換
export const formatDateForGoogleCalendar = (date: Date) => {
  return date
    .toISOString()
    .replace(/[-:]/g, '')
    .replace(/\.\d{3}/, '')
}

export const convertDateToTimestamp = (date: string | null | undefined) => {
  if (!date) {
    return
  }
  const d = new Date(date)
  // 無効な日付 / proto 範囲外は undefined を返す。
  // 範囲外を素通すと protoInt64.parse(NaN) や後段の toJsonString で throw する。
  if (!isValidDate(d)) {
    return
  }
  const unixTimestamp = Math.floor(d.getTime() / 1000)
  return create(TimestampSchema, {
    seconds: protoInt64.parse(unixTimestamp),
    nanos: 0,
  })
}

/**
 * Date型のタイムスタンプを'YYYY/MM/DD HH:mm'形式にフォーマットする
 * @param timestamp Date型のタイムスタンプ
 * @returns フォーマットされた文字列。undefinedの場合は空文字列
 */
export const formatTimestamp = (timestamp: Date | undefined): string => {
  if (!timestamp) {
    return ''
  }
  const year = timestamp.getFullYear()
  const month = String(timestamp.getMonth() + 1).padStart(2, '0')
  const day = String(timestamp.getDate()).padStart(2, '0')
  const hours = String(timestamp.getHours()).padStart(2, '0')
  const minutes = String(timestamp.getMinutes()).padStart(2, '0')
  return `${year}/${month}/${day} ${hours}:${minutes}`
}
